import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useFormikWithoutDirtyForm } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addDays, startOfISOWeek } from "date-fns";

import { AppointmentResponse, PurposeType, appointmentsApi } from "@/api";
import {
  AttendanceEntryFormValues,
  WeeklyPlanningFormValues,
} from "@/common/components/appointments/plan-attendance/PlanAttendanceDialog.types";
import {
  buildInitialDays,
  createEmptyEntry,
} from "@/common/components/appointments/plan-attendance/PlanAttendanceDialog.utils";
import {
  buildWeeklyPlanningEntries,
  weeklyPlanningValidationSchema,
} from "@/common/components/appointments/plan-attendance/components/weekly-planning-form/WeeklyPlanningForm.utils";
import { useAttendancePurposes } from "@/common/components/appointments/plan-attendance/hooks/useAttendancePurposes";
import { useCurrentUserContext } from "@/common/contexts/current-user-context/CurrentUserContext";
import { getLocalTimeZoneId } from "@/common/utils/getLocalTimeZoneId";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

function buildAllTouched(values: WeeklyPlanningFormValues) {
  const entryTouched = {
    key: true,
    purposeKey: true,
    attendanceType: true,
    startTime: true,
    endTime: true,
    note: true,
    substituteCounselorId: true,
  };
  return { days: values.days.map((d) => ({ entries: d.entries.map(() => entryTouched) })) };
}

export function useWeeklyPlanningForm(initialWeekStart: Date, onClose: () => void, enabled = true) {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const queryClient = useQueryClient();
  const { currentUser } = useCurrentUserContext();
  const { purposes, purposeOptions, isLoading: isPurposesLoading } = useAttendancePurposes();

  const [weekStart, setWeekStart] = useState<Date>(() => startOfISOWeek(initialWeekStart));
  const weekKey = weekStart.toISOString();
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const savedWeeks = useRef<Record<string, WeeklyPlanningFormValues>>({});

  const { data: weekAppointmentsData, isLoading: isAppointmentsLoading } = useQuery<AppointmentResponse[]>({
    queryKey: [
      QueryKey.Appointments,
      weekStart.toISOString(),
      weekEnd.toISOString(),
      PurposeType.Attendance,
      currentUser.counselorId,
    ],
    queryFn: () =>
      appointmentsApi.getAppointmentsByDate({
        startDate: weekStart,
        endDate: weekEnd,
        assignedCounselorId: currentUser.counselorId,
        purposeType: PurposeType.Attendance,
      }),
    enabled,
  });

  const planMutation = useMutation({
    mutationFn: (submissions: Array<{ weekStart: Date; entries: ReturnType<typeof buildWeeklyPlanningEntries> }>) =>
      appointmentsApi.planWeeklyAttendanceBatch({
        planWeeklyAttendanceRequest: submissions.map((s) => ({
          weekStart: s.weekStart,
          originTimeZoneId: getLocalTimeZoneId(),
          entries: s.entries,
        })),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Appointments] });
      showSuccess(t("calendar.planAttendance.successMessage"));
      onClose();
    },
  });

  const formik = useFormikWithoutDirtyForm<WeeklyPlanningFormValues>({
    initialValues: { days: [] },
    validationSchema: weeklyPlanningValidationSchema(t),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values) => {
      savedWeeks.current[weekKey] = values;
      const submissions = Object.entries(savedWeeks.current).map(([key, weekVals]) => ({
        weekStart: new Date(key),
        entries: buildWeeklyPlanningEntries(weekVals.days, purposes),
      }));
      await planMutation.mutateAsync(submissions);
    },
  });

  const { resetForm, validateForm, setTouched, setValues, values } = formik;

  const buildInitialDaysForCurrentWeek = useCallback(
    (data: AppointmentResponse[]) => buildInitialDays(weekStart, data),
    [weekStart]
  );

  useEffect(() => {
    const saved = savedWeeks.current[weekKey];
    if (saved) {
      resetForm({ values: saved });
    } else if (weekAppointmentsData !== undefined) {
      resetForm({ values: { days: buildInitialDaysForCurrentWeek(weekAppointmentsData) } });
    }
  }, [weekKey, weekAppointmentsData, buildInitialDaysForCurrentWeek, resetForm]);

  const validateBeforeNavigate = useCallback(async (): Promise<boolean> => {
    const errors = await validateForm();
    if (Object.keys(errors).length > 0) {
      await setTouched(buildAllTouched(values));
      return false;
    }
    return true;
  }, [validateForm, setTouched, values]);

  const navigateWeek = useCallback(
    async (direction: "prev" | "next") => {
      const valid = await validateBeforeNavigate();
      if (!valid) {
        return;
      }
      savedWeeks.current[weekKey] = values;
      setWeekStart((d) => addDays(d, direction === "next" ? 7 : -7));
    },
    [weekKey, values, validateBeforeNavigate]
  );

  const updateDayEntries = useCallback(
    async (dayIndex: number, updater: (entries: AttendanceEntryFormValues[]) => AttendanceEntryFormValues[]) => {
      await setValues((prev) => ({
        ...prev,
        days: prev.days.map((day, i) => (i === dayIndex ? { ...day, entries: updater(day.entries) } : day)),
      }));
    },
    [setValues]
  );

  const addEntry = useCallback(
    async (dayIndex: number) => updateDayEntries(dayIndex, (entries) => [...entries, createEmptyEntry()]),
    [updateDayEntries]
  );

  const removeEntry = useCallback(
    async (dayIndex: number, entryIndex: number) =>
      updateDayEntries(dayIndex, (entries) => entries.filter((_, i) => i !== entryIndex)),
    [updateDayEntries]
  );

  return {
    formik,
    weekStart,
    purposes,
    purposeOptions,
    navigateWeek,
    addEntry,
    removeEntry,
    updateDayEntries,
    isSubmitting: planMutation.isPending,
    isLoading: isPurposesLoading || isAppointmentsLoading,
  };
}
